import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reads and writes a view's state in the URL query string, so search, filters, paging
 * and sorting survive opening a detail page and coming back, survive a refresh, and can
 * be shared as a link.
 *
 * @param defaults Value per param that is left out of the URL, keeping links tidy.
 *                 Declare it at module scope — a fresh object each render defeats memoization.
 */
export function useUrlParams(defaults: Record<string, string> = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // React Router builds each update from the params of the render it was called in, not
  // from any pending update, so two patches in one handler would see the same base and
  // the second would silently drop the first — `setFilter(x); setPage(1)` losing the
  // filter. Accumulate writes here until the URL catches up with them.
  const pendingRef = useRef<URLSearchParams | null>(null);
  useEffect(() => {
    pendingRef.current = null;
  }, [searchParams]);

  const get = useCallback(
    (key: string) => searchParams.get(key) ?? defaults[key] ?? '',
    [searchParams, defaults],
  );

  /**
   * Merge params into the URL, replacing the current history entry — typing a search
   * term must not stack one entry per keystroke, or Back would crawl through the
   * letters instead of leaving the page.
   */
  const patch = useCallback(
    (values: Record<string, string | number>) => {
      const next = new URLSearchParams(pendingRef.current ?? searchParams);
      for (const [key, value] of Object.entries(values)) {
        const str = String(value);
        if (str === '' || defaults[key] === str) next.delete(key);
        else next.set(key, str);
      }
      pendingRef.current = next;
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, defaults],
  );

  return { get, patch };
}
